import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { ArrowRight } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import { INSIGHTS } from '@/data/insights';

function InsightsHero() {
  const { t } = useLanguage();
  const chips = ['insights.chip1', 'insights.chip2', 'insights.chip3'];
  return (
    <section data-testid="insights-hero" className="relative py-24 md:py-32 bg-[#080B10] overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(600px circle at 15% 25%, rgba(99,102,241,0.14), transparent 60%), radial-gradient(700px circle at 85% 75%, rgba(129,140,248,0.10), transparent 60%)',
        }}
      />
      <div
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
        }}
      />
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <p className="text-xs md:text-sm uppercase tracking-[0.25em] font-bold text-[#6366F1] mb-6">
          {t('insights.label')}
        </p>
        <h1
          data-testid="insights-page-title"
          className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-bold text-white leading-tight mb-6"
          style={{ fontFamily: "'Plus Jakarta Sans', 'Noto Sans TC', sans-serif" }}
        >
          {t('insights.title')}
        </h1>
        <p className="text-base md:text-lg text-[#94A3B8] leading-relaxed max-w-3xl mb-8" style={{ fontFamily: "'Manrope', 'Noto Sans TC', sans-serif" }}>
          {t('insights.subtitle')}
        </p>
        <div className="flex flex-wrap gap-3">
          {chips.map((key) => (
            <span key={key} className="px-4 py-1.5 rounded-full bg-white/5 backdrop-blur-md border border-white/10 text-xs font-semibold text-white/60">
              {t(key)}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturedCard({ insight, meta }) {
  const { t } = useLanguage();
  return (
    <Link
      to={insight.path}
      data-testid={`insight-card-${insight.id}`}
      className="group block bg-[#0E131F] border border-slate-800/60 rounded-3xl p-8 md:p-12 hover:-translate-y-1 hover:border-[#6366F1]/30 transition-all duration-300 shadow-lg relative overflow-hidden"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: `radial-gradient(480px circle at 85% 20%, ${insight.accent}1f, transparent 60%)` }}
      />
      <div className="relative z-10">
        <div className="flex items-center gap-3 mb-5">
          <span
            className="inline-block px-3 py-1 rounded-full text-xs font-bold tracking-wide"
            style={{ backgroundColor: `${insight.accent}1a`, border: `1px solid ${insight.accent}4d`, color: insight.accent }}
          >
            {meta.category}
          </span>
          <span className="text-xs font-bold uppercase tracking-widest text-white/60">{t('insights.featured')}</span>
        </div>
        <h2
          className="text-2xl md:text-3xl lg:text-4xl tracking-tight font-bold text-white leading-snug mb-4 group-hover:text-[#818CF8] transition-colors max-w-3xl"
          style={{ fontFamily: "'Plus Jakarta Sans', 'Noto Sans TC', sans-serif" }}
        >
          {meta.title}
        </h2>
        <p className="text-sm md:text-base text-[#94A3B8] leading-relaxed mb-6 max-w-2xl" style={{ fontFamily: "'Manrope', 'Noto Sans TC', sans-serif" }}>
          {meta.desc}
        </p>
        <div className="flex items-center justify-between">
          <span className="text-xs text-white/70">{meta.date} · DZent Limited</span>
          <span className="inline-flex items-center gap-2 text-sm font-semibold" style={{ color: insight.accent }}>
            {t('insights.read')}
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </span>
        </div>
      </div>
    </Link>
  );
}

function InsightCard({ insight, meta }) {
  const { t } = useLanguage();
  return (
    <Link
      to={insight.path}
      data-testid={`insight-card-${insight.id}`}
      className="group flex flex-col bg-[#0E131F] border border-slate-800/60 rounded-3xl p-8 hover:border-[#6366F1]/30 hover:-translate-y-1 transition-all duration-300"
    >
      <span
        className="self-start inline-block px-3 py-1 rounded-full text-xs font-bold tracking-wide mb-5"
        style={{ backgroundColor: `${insight.accent}14`, border: `1px solid ${insight.accent}40`, color: insight.accent }}
      >
        {meta.category}
      </span>
      <h3
        className="text-lg md:text-xl tracking-tight font-bold text-[#F8FAFC] leading-snug mb-3 group-hover:text-[#6366F1] transition-colors"
        style={{ fontFamily: "'Plus Jakarta Sans', 'Noto Sans TC', sans-serif" }}
      >
        {meta.title}
      </h3>
      <p className="text-sm text-[#94A3B8] leading-relaxed mb-6 flex-1" style={{ fontFamily: "'Manrope', 'Noto Sans TC', sans-serif" }}>
        {meta.desc}
      </p>
      <div className="flex items-center justify-between">
        <span className="text-xs text-[#64748B]">{meta.date}</span>
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#94A3B8] group-hover:text-[#6366F1] transition-colors">
          {t('insights.read')}
          <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
        </span>
      </div>
    </Link>
  );
}

export default function InsightsPage() {
  const { t, lang } = useLanguage();
  const [featured, ...rest] = INSIGHTS;
  return (
    <div data-testid="insights-page" className="pt-20">
      <SEOHead path="/insights" />
      <InsightsHero />
      <section className="py-20 md:py-28 bg-[#080B10] border-t border-slate-800/60">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FeaturedCard insight={featured} meta={featured.meta[lang] || featured.meta.en} />
          <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#6366F1] mt-16 mb-8">
            {t('insights.all')}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {rest.map((insight) => (
              <InsightCard key={insight.id} insight={insight} meta={insight.meta[lang] || insight.meta.en} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
